import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

async function getCroppedBlob(src, area) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
  const canvas = document.createElement("canvas");
  const outW = 800, outH = 600; // fixed 4:3 output
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, outW, outH);
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.9));
}

// Opens a 4:3 cropper for a selected File; calls onCropped(Blob) when confirmed.
export default function ImageCropper({ file, aspect = 4 / 3, onCancel, onCropped }) {
  const [src] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPx, setAreaPx] = useState(null);
  const [busy, setBusy] = useState(false);
  const onComplete = useCallback((_, px) => setAreaPx(px), []);

  const confirm = async () => {
    if (!areaPx) return;
    setBusy(true);
    const blob = await getCroppedBlob(src, areaPx);
    const cropped = new File([blob], (file.name || "image").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
    onCropped(cropped);
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Crop image</DialogTitle></DialogHeader>
        <div className="relative w-full h-72 bg-black rounded-lg overflow-hidden" data-testid="cropper-area">
          <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
        </div>
        <div className="px-1">
          <p className="text-xs text-muted-foreground mb-1">Zoom</p>
          <Slider min={1} max={3} step={0.01} value={[zoom]} onValueChange={(v) => setZoom(v[0])} data-testid="cropper-zoom" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="cropper-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={busy} data-testid="cropper-confirm">{busy ? "Saving…" : "Use photo"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
